import moment from "moment";
import { ArbeitsplanType, Terminarten } from "./types";
import _ from "lodash";

export function getEarliestTime(workPlanList: ArbeitsplanType[]): string {
  return _.minBy(workPlanList, (plan: ArbeitsplanType) => {
    const date = getHoursFromGesamtTermin(plan.zeitvon);
    return date;
  }).zeitvon;
}

export function getDateInfosList(workPlanList: ArbeitsplanType[]): {
  earliestDateMoment: moment.Moment;
  latestDateMoment: moment.Moment;
  isCurrentDateInRange: boolean;
} {
  const earliestDatePlan = _.minBy(workPlanList, (plan: ArbeitsplanType) => {
    const date = moment(plan.gesamttermindatumvon, "YYYY-MM-DD");
    const date2 = moment(plan.datum, "YYYY-MM-DD");
    const unix1 = date.isValid() ? date.unix() : Infinity;
    const unix2 = date2.isValid() ? date2.unix() : Infinity;
    return Math.min(unix1, unix2);
  });

  const earliestDateMoment = earliestDatePlan
    ? moment.min(
        [
          moment(earliestDatePlan.gesamttermindatumvon, "YYYY-MM-DD"),
          moment(earliestDatePlan.datum, "YYYY-MM-DD")
        ].filter(m => m.isValid())
      )
    : moment();

  const latestDatePlan = _.maxBy(workPlanList, (plan: ArbeitsplanType) => {
    const date = moment(plan.gesamttermindatumbis, "YYYY-MM-DD");
    const date2 = moment(plan.datum, "YYYY-MM-DD");
    const unix1 = date.isValid() ? date.unix() : -Infinity;
    const unix2 = date2.isValid() ? date2.unix() : -Infinity;
    return Math.max(unix1, unix2);
  });

  const latestDateMoment = latestDatePlan
    ? moment.max(
        [
          moment(latestDatePlan.gesamttermindatumbis, "YYYY-MM-DD"),
          moment(latestDatePlan.datum, "YYYY-MM-DD")
        ].filter(m => m.isValid())
      )
    : moment();

  const currentDate = moment();

  const isBefore = earliestDateMoment.isSameOrBefore(currentDate, "day");
  const isAfter = latestDateMoment.isSameOrAfter(currentDate, "day");

  const isCurrentDateInRange = isBefore && isAfter;

  return { earliestDateMoment, latestDateMoment, isCurrentDateInRange };
}

export function getIsCurrentDateInRange(
  workPlan: ArbeitsplanType,
  currentDate = moment()
): boolean {
  return workPlan.gesamttermindatumbis
    ? moment(workPlan.gesamttermindatumvon, "YYYY-MM-DD").isSameOrBefore(
        currentDate,
        "day"
      ) &&
        moment(workPlan.gesamttermindatumbis, "YYYY-MM-DD").isSameOrAfter(
          currentDate,
          "day"
        )
    : moment(workPlan.datum, "YYYY-MM-DD").isSame(currentDate, "day");
}

export function getIsAnyDateInRange(
  workPlan: ArbeitsplanType,
  dates: moment.Moment[]
): boolean {
  return dates.some((date) =>
    getIsCurrentDateInRange(workPlan, date ?? moment())
  );
}

export function getHoursFromGesamtTermin(gesamttermin: string): number {
  const parts = gesamttermin.split(":");
  const hours = parseInt(parts[0]);
  const minutes = parseInt(parts[1]);
  const seconds = parseFloat(parts[2]);
  return hours + minutes / 60 + seconds / 3600;
}

export function replaceEnglishMonthWithGerman(month: string): string {
  return month
    .replace("January", "Januar")
    .replace("February", "Februar")
    .replace("March", "März")
    .replace("April", "April")
    .replace("May", "Mai")
    .replace("June", "Juni")
    .replace("July", "Juli")
    .replace("August", "August")
    .replace("September", "September")
    .replace("October", "Oktober")
    .replace("November", "November")
    .replace("December", "Dezember");
}

export function getArbeitsplanTitle(workPlan: ArbeitsplanType): string {
  if (!workPlan) return "";
  const terminartKey = Object.keys(Terminarten).find(
    (key) => Terminarten[key] === workPlan.terminart
  );
  const objekt_name =
    workPlan.objekt_name?.trim().length > 0
      ? workPlan.objekt_name.trim()
      : null;
  const kunde_name =
    workPlan.kunde_name?.trim().length > 0 ? workPlan.kunde_name.trim() : null;
  return (
    (objekt_name ?? kunde_name ?? terminartKey) +
    (workPlan.vorgang ? ", " + workPlan.vorgang : "")
  );
}

export function getArbeitsplanTitleNoVorgang(
  workPlan: ArbeitsplanType
): string {
  if (!workPlan) return "";
  const terminartKey = Object.keys(Terminarten).find(
    (key) => Terminarten[key] === workPlan.terminart
  );
  const objekt_name =
    workPlan.objekt_name?.trim().length > 0
      ? workPlan.objekt_name.trim()
      : null;
  const kunde_name =
    workPlan.kunde_name?.trim().length > 0 ? workPlan.kunde_name.trim() : null;

  return objekt_name ?? kunde_name ?? terminartKey;
}

export function getFullHourTimeFrame(workPlanList: ArbeitsplanType[]) {
  //get earliest and latest hour from zeitvon and zeitbis
  const earliestHour = _.minBy(workPlanList, (plan: ArbeitsplanType) => {
    const date = getHoursFromGesamtTermin(plan.zeitvon);
    return date;
  });
  const latestHour = _.maxBy(workPlanList, (plan: ArbeitsplanType) => {
    const date = getHoursFromGesamtTermin(plan.zeitbis);
    return date;
  });
  return (
    "" +
    earliestHour?.zeitvon.slice(0, 5) +
    " - " +
    latestHour?.zeitbis.slice(0, 5)
  );
}

export const getCSRFToken = () => {
  let csrfToken = null;
  const cookies = document.cookie.split(";");
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split("=");
    if (name === "csrftoken") {
      csrfToken = value;
      break;
    }
  }
  return csrfToken;
};
